import React, { useLayoutEffect } from 'react';

import cn from 'classnames';

import { useGetJournalEventsContext } from '@entities/eventJournal/api/hooks';
import { eventJournalSortStorageKey } from '@entities/eventJournal/model/globals';
import { CalendarPeriodPicker, JournalEventsFilters, JournalEventsSearch } from '@features';
import { useSortQueryState } from '@features/JournalSortFilter/lib/hooks';
import { getFromLocalStorage } from '@shared/helpers';
import { Lightbox, useLightBoxControl } from '@shared/ui/Lightbox';
import { ExportPdfModal } from '@widgets/EventJournal/ui/ExportPdfModal';
import { PreviewFileHeader } from '@widgets/EventJournal/ui/PreviewFileHeader';

import cls from './EventJournal.module.css';
import { useEventJournal } from './lib/hooks/useEventJournal';
import { EventJournalHeader, EventJournalBody, EventJournalModal } from './ui';

interface EventJournalProps {
    isOwner: boolean;
    classes?: string;
}

export const EventJournal = ({ isOwner, classes }: EventJournalProps) => {
    useEventJournal();
    const { data: filtersData, isPending } = useGetJournalEventsContext();
    const { setSortQueryState } = useSortQueryState();

    const { onCloseLightBox, files, currentFileId, currentFile } = useLightBoxControl();

    useLayoutEffect(() => {
        const sortValue = getFromLocalStorage(eventJournalSortStorageKey);

        void setSortQueryState(sortValue || 'asc');
    }, [setSortQueryState]);

    return (
        <main className={cn(cls.EventJournal, [classes])}>
            <div className={cls.content}>
                <EventJournalHeader
                    canCreateCustomEvent={filtersData?.canCreateCustomEvent}
                    isOwner={isOwner}
                />
                <EventJournalBody
                    canCreateCustomEvent={filtersData?.canCreateCustomEvent}
                    isOwner={isOwner}
                />
            </div>
            <div className={cls.contentWrapper}>
                <JournalEventsSearch isPending={isPending} />
                <CalendarPeriodPicker isPending={isPending} />
                <JournalEventsFilters
                    filtersData={{
                        types: filtersData?.types,
                        authors: filtersData?.authors,
                        estimates: filtersData?.estimates,
                    }}
                    isPending={isPending}
                />
            </div>
            <EventJournalModal estimates={filtersData?.estimates} />
            <ExportPdfModal />
            {!!currentFileId && currentFile && (
                <Lightbox
                    currentFileId={currentFileId}
                    onClose={onCloseLightBox}
                    files={files}
                    headerSlot={
                        <PreviewFileHeader
                            onClose={onCloseLightBox}
                            fileSrc={currentFile.fileSrc}
                            fileName={currentFile.fileName}
                        />
                    }
                />
            )}
        </main>
    );
};
